import type { DeepReadonly, ToRefs } from 'vue'
import { reactive, readonly, toRefs } from 'vue'

import type { DrillDataLocalization } from '~/entities/drillLabel'
import type { DrillDataSetManager } from '~/lib/three-d-resources/drill-data-set-manager'
import type { Drill } from '~/entities/drill'
import type { Option } from '@/components/VDropdown.vue'

type State = {
  localizations: DrillDataLocalization[]
  selectedLocalization: DrillDataLocalization | undefined
}

export type UseLocalizations = {
  currentLocalization: Ref<Option | null>
  createLocalization(localizationData: Partial<DrillDataLocalization>): Promise<void>
  updateLocalization(): Promise<void>
  localizationToString(option: Option): string
} & ToRefs<DeepReadonly<State>>

export function useLocalizations({
  companyId,
  drillDataSet,
  initialDrillDataSetLayerGroup,
  drillDataSetManager,
}: {
  companyId: number
  drillDataSet: Drill | null
  initialDrillDataSetLayerGroup: DrillDataSetLayerGroup | null
  drillDataSetManager: DrillDataSetManager | null
}): UseLocalizations {
  const state = reactive<State>({
    localizations: [],
    selectedLocalization: undefined,
  })

  const currentLocalization = ref<Option | null>(null)

  onMounted(async () => {
    state.localizations = await $fetch<DrillDataLocalization[]>(`/v2/drill/companies/${companyId}/localizations`, {
      ...useFetchSharedOptions(),
      params: { sort_by: '-created_at' },
    })

    if (initialDrillDataSetLayerGroup) {
      currentLocalization.value = state.localizations.find(l => l.id === initialDrillDataSetLayerGroup!.localizationId) ?? null
      return
    }

    if (state.localizations.length) currentLocalization.value = state.localizations[0]
  })

  watch(currentLocalization, () => {
    state.selectedLocalization = state.localizations.find(l => l.id === currentLocalization.value?.id)
  })

  function localizationToString(option: Option): string {
    const localization = state.localizations.find(l => l.id === option.id)

    if (!localization) return ''

    let unitsOfMeasurement: string = localization.unit_of_measurement

    if (unitsOfMeasurement === 'm') {
      unitsOfMeasurement = 'Metres'
    } else if (unitsOfMeasurement === 'f') {
      unitsOfMeasurement = 'Feet'
    }

    return `X: ${localization.easting}, Y: ${localization.northing}, Z: ${localization.elevation}, Azimuth: ${localization.rotation}, ${unitsOfMeasurement}`
  }

  async function updateLocalization() {
    if (!state.selectedLocalization) return

    const dataSet =
      drillDataSet ?? drillDataSetManager!.drillDataSets.find(d => d.data_set_id === initialDrillDataSetLayerGroup?.dataSetId)?.data_set

    if (initialDrillDataSetLayerGroup) {
      await drillDataSetManager!.removeDataSet(initialDrillDataSetLayerGroup!.dataSetId)
    }

    if (dataSet) {
      await drillDataSetManager!.addDataSet({
        drillDataSet: dataSet,
        localization: state.selectedLocalization!,
      })
    }

    drillDataSetManager!.setSceneIdToCurrent()
  }

  async function createLocalization(localizationData: Partial<DrillDataLocalization>) {
    if (!drillDataSetManager) return

    const localization = await $fetch<DrillDataLocalization>(`v2/drill/companies/${companyId}/localizations`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: JSON.stringify(localizationData),
    })

    const dataSet =
      drillDataSet ?? drillDataSetManager.drillDataSets.find(d => d.data_set_id === initialDrillDataSetLayerGroup?.dataSetId)?.data_set

    if (initialDrillDataSetLayerGroup) {
      await drillDataSetManager.removeDataSet(initialDrillDataSetLayerGroup.dataSetId)
    }

    if (dataSet) {
      await drillDataSetManager.addDataSet({
        drillDataSet: dataSet,
        localization,
      })
    }
  }

  return {
    ...toRefs(readonly(state)),
    currentLocalization,
    localizationToString,
    createLocalization,
    updateLocalization,
  }
}
